import { Stack } from '@mui/material';
import { logger } from '../../services/logger/logger';
import { ColDef } from 'ag-grid-community';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import Chips from '../../components/common/Chips';
import ClientSideAgGrid from '../../components/common/customAgGrid/ClientSideAgGrid';
import CustomActionButton from '../../components/common/customAgGrid/CustomActionButton';
import CustomAgDateFilter from '../../components/common/customAgGrid/CustomAgDateFilter';
import CustomAgMultiSelectFilter from '../../components/common/customAgGrid/CustomAgMultiSelectFilter';
import CustomAgTextFilter from '../../components/common/customAgGrid/CustomAgTextFilter';
import DeleteConfirmModal from '../../components/common/DeleteConfirmModal';
import EditLubricantProductModal from '../../components/lubricants/EditLubricantProductModal';
import PageHeader from '../../components/PageHeader';
import { useGetCompaniesMakersListQuery } from '../../store/apiSlices/companies/companiesMakers/companiesMakersApis';
import { useGetLubricantCategoriesListQuery } from '../../store/apiSlices/lubricants/lubricantCategory/lubricantCategoryApis';
import { useGetLubricantGroupsListQuery } from '../../store/apiSlices/lubricants/lubricantGroups/lubricantGroupsApis';
import {
  useCreateLubricantProductMutation,
  useDeleteLubricantProductMutation,
  useGetLubricantProductsQuery,
  useGetUnitsOfMeasurementQuery,
  useUpdateLubricantProductMutation,
} from '../../store/apiSlices/lubricants/lubricantProducts/lubricantProductsApis';
import { useGetLubricantTypesListQuery } from '../../store/apiSlices/lubricants/lubricantTypes/lubricantTypesApis';
import { helpingMethod } from '../../utils/HelpingMethod';
import { canEditReferenceData } from '../../utils/permissions';
import { showSuccessToast } from '../../components/common/BaseToaster';
import { TableCardContent } from '../../components/common/styled/TableCardContent';
import { useDeleteConfirmation } from '../../hooks/useDeleteConfirmation';
import { useTranslation } from 'react-i18next';

// ------------------- Types -------------------
interface LubricantProductListItem {
  id: number;
  productName: string;
  maker: string;
  makerName: string;
  category: string;
  categoryName: string;
  type: string;
  typeName: string;
  unit: string;
  unitName: string;
  group: string;
  groupName: string;
  status: string;
  lastUpdatedBy: string;
  lastUpdatedOn: string;
}

interface ActionButton {
  label: string;
  variant: 'text' | 'blue' | 'outlinedBlue' | 'red' | 'gray' | undefined;
  onClick: () => void;
}

interface BreadcrumbOption {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

// ------------------- Component -------------------
const LubricantProductsContainer: React.FC = () => {
  const { t } = useTranslation();
  const userInfo = useSelector((state: any) => state.user.userInfo);
  const canAddorEdit = canEditReferenceData(userInfo?.authorityName);
  const [showAddProductModal, setShowAddProductModal] = useState<boolean>(false);
  const [editingProduct, setEditingProduct] = useState<LubricantProductListItem | null>(null);
  type ProductFormValues = {
    productName: string;
    maker: string;
    category: string;
    type: string;
    group: string;
    unit: string;
    status: string;
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ProductFormValues>({
    defaultValues: {
      productName: '',
      maker: '',
      category: '',
      type: '',
      group: '',
      unit: '',
      status: 'Active',
    },
  });
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [resetFiltersSignal, setResetFiltersSignal] = useState(false);
  const [tableColumns, setTableColumns] = useState<ColDef[]>([]);

  // API calls
  const {
    data: lubricantProductsResponse,
    isLoading,
    refetch,
  } = useGetLubricantProductsQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  const [createLubricantProduct, { isLoading: isCreating }] = useCreateLubricantProductMutation();
  const [updateLubricantProduct, { isLoading: isUpdating }] = useUpdateLubricantProductMutation();
  const [deleteLubricantProduct, { isLoading: isDeleting }] = useDeleteLubricantProductMutation();

  const { showDeleteConfirm, handleConfirmDelete, onDeleteClick, onCloseDeleteConfirm } =
    useDeleteConfirmation<LubricantProductListItem>({
      deleteMutation: deleteLubricantProduct,
      refetch,
      itemName: 'product',
      idField: 'id',
    });

  // Dropdown data APIs
  const { data: makersData } = useGetCompaniesMakersListQuery(
    { active: true },
    {
      refetchOnMountOrArgChange: true,
    },
  );
  const { data: categoriesData } = useGetLubricantCategoriesListQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const { data: typesData } = useGetLubricantTypesListQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const { data: groupsData } = useGetLubricantGroupsListQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const { data: unitsData } = useGetUnitsOfMeasurementQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  // Transform dropdown data
  // When editing, include active makers + the current row's maker (even if inactive)
  // When adding, use only active makers
  const makersOptions = useMemo(() => {
    if (!makersData) return [];

    const activeMakers = makersData.map((maker: any) => ({
      value: maker.id.toString(),
      label: maker.shortName,
    }));

    // If editing, check if the current maker is already in active list
    if (editingProduct && editingProduct.maker) {
      const currentMakerId = editingProduct.maker.toString();
      const isCurrentMakerInActiveList = activeMakers.some((m: any) => m.value === currentMakerId);

      // If current maker is not in active list, add it using the makerName from row data
      if (!isCurrentMakerInActiveList && editingProduct.makerName) {
        return [
          ...activeMakers,
          {
            value: currentMakerId,
            label: editingProduct.makerName,
          },
        ];
      }
    }

    return activeMakers;
  }, [makersData, editingProduct]);

  const categoriesOptions = useMemo(() => {
    if (!categoriesData?.data) return [];
    return categoriesData.data.map((category: any) => ({
      value: category.id.toString(),
      label: category.categoryName,
    }));
  }, [categoriesData]);

  const typesOptions = useMemo(() => {
    if (!typesData?.data) return [];
    return typesData.data.map((type: any) => ({
      value: type.id.toString(),
      label: type.name,
    }));
  }, [typesData]);

  const groupsOptions = useMemo(() => {
    if (!groupsData?.data) return [];
    return groupsData.data.map((group: any) => ({
      value: group.id.toString(),
      label: group.groupName,
    }));
  }, [groupsData]);

  const unitsOptions = useMemo(() => {
    if (!unitsData?.data) return [];
    return unitsData.data
      .map((unit: any) => ({
        value: unit.id.toString(),
        label: unit.symbol,
      }))
      ?.filter((unit: any) => ['kg', 'L']?.includes(unit.label));
  }, [unitsData]);

  // Transform API data to display format
  const lubricantProductsList = useMemo(() => {
    if (!lubricantProductsResponse?.data) return [];

    const responseData = lubricantProductsResponse.data as any;
    const dataArray = Array.isArray(responseData) ? responseData : responseData?.content || [];

    if (!Array.isArray(dataArray)) {
      return [];
    }

    return dataArray.map((product: any) => {
      const makerNameFromApi = product.makerName;
      const makerOption = makersOptions.find((opt) => opt.value === product.makerId.toString());
      const makerName = makerNameFromApi || makerOption?.label || product.makerId.toString();

      return {
        id: product.id,
        productName: product.name,
        maker: product.makerId.toString(),
        makerName: makerName,
        category: product.categoryId.toString(),
        categoryName: product.categoryName || '-',
        type: product.typeId?.toString() || '',
        typeName: product.typeName || '-',
        unit: product.uomId.toString(),
        unitName: product.symbol || '-',
        group: product.productGroupId?.toString() || '',
        groupName: product.productGroupName || '-',
        status: product.active ? 'Active' : 'Inactive',
        lastUpdatedBy: product?.updatedBy?.toString() || '-',
        lastUpdatedOn: helpingMethod.formatDate(product.updatedOn) || '-',
      };
    });
  }, [lubricantProductsResponse, makersOptions]);

  // Page header action buttons
  const actionBtns: ActionButton[] = canAddorEdit
    ? [
        {
          label: t('lubricants.addProduct'),
          variant: 'blue',
          onClick: () => setShowAddProductModal(true),
        },
      ]
    : [];

  // Handle edit button click
  const handleEditClick = useCallback(
    (rowData: LubricantProductListItem) => {
      setEditingProduct(rowData);
      reset({
        productName: rowData.productName,
        maker: rowData.maker,
        category: rowData.category,
        type: rowData.type,
        group: rowData.group,
        unit: rowData.unit,
        status: rowData.status,
      });
      setShowAddProductModal(true);
    },
    [reset],
  );

  // Table columns
  const columns: ColDef[] = useMemo(
    () => [
      {
        field: 'id',
        headerName: t('table.columns.id'),
        sortable: true,
        width: 120,
        maxWidth: 120,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgTextFilter,
      },
      {
        field: 'productName',
        headerName: t('table.columns.productName'),
        width: 200,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'makerName',
        headerName: t('table.columns.maker'),
        width: 150,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'categoryName',
        headerName: t('table.columns.category'),
        width: 150,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'typeName',
        headerName: t('table.columns.type'),
        width: 150,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'unitName',
        headerName: t('table.columns.unit'),
        width: 120,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'groupName',
        headerName: t('table.columns.group'),
        width: 150,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'lastUpdatedBy',
        headerName: t('table.columns.lastUpdatedBy'),
        width: 170,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'lastUpdatedOn',
        headerName: t('table.columns.lastUpdatedOn'),
        width: 160,
        filter: 'agDateColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgDateFilter,
        filterParams: {
          suppressAndOrCondition: true,
          maxNumConditions: 1,
          filterOptions: ['equals'],
          defaultOption: 'equals',
        },
      },
      {
        field: 'status',
        headerName: t('table.columns.status'),
        width: 150,
        maxWidth: 150,
        filter: true,
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
        cellRenderer: (params: any) => <Chips width="100px" label={params?.data?.status} />,
      },
      {
        field: 'actions',
        headerName: '',
        width: 50,
        maxWidth: 50,
        flex: 0,
        sortable: false,
        pinned: 'right',
        cellRenderer: (params: any) => (
          <CustomActionButton
            menuOptions={[
              {
                label: t('buttons.edit'),
                onClick: () => handleEditClick(params.data),
              },
              {
                label: t('buttons.delete'),
                onClick: () => onDeleteClick(params.data),
              },
            ]}
          />
        ),
      },
    ],
    [handleEditClick, onDeleteClick, t],
  );

  useEffect(() => {
    setTableColumns(canAddorEdit ? columns : columns.filter((col) => col.field !== 'actions'));
  }, [canAddorEdit, columns]);

  // Breadcrumb options
  const breadcrumbOptions: BreadcrumbOption[] = [
    { label: t('lubricants.products'), isActive: true, onClick: () => {} },
  ];

  // Form submission handler
  const onSubmit = useCallback(
    async (values: ProductFormValues) => {
      try {
        if (editingProduct) {
          const updatePayload = {
            productName: values.productName,
            makerId: parseInt(values.maker),
            categoryid: parseInt(values.category), // Note: lowercase 'categoryid' for PUT API
            typeId: parseInt(values.type),
            productGroupId: parseInt(values.group),
            uomId: parseInt(values.unit),
            status: values.status === 'Active',
            // updatedBy: 1, // Default value as per requirements
          };

          const res: any = await updateLubricantProduct({
            id: editingProduct.id,
            payload: updatePayload,
          }).unwrap();
          showSuccessToast(res?.message);
        } else {
          const createPayload = {
            name: values.productName,
            makerId: parseInt(values.maker),
            categoryId: parseInt(values.category),
            typeId: parseInt(values.type),
            productGroupId: parseInt(values.group),
            typeInManagerId: null, // Default value as per requirements
            uomId: parseInt(values.unit),
            status: values.status === 'Active',
            typeIn: null, // Default value as per requirements
          };

          const res: any = await createLubricantProduct(createPayload).unwrap();
          showSuccessToast(res?.message);
        }

        setShowAddProductModal(false);
        setEditingProduct(null);
        reset({
          productName: '',
          maker: '',
          category: '',
          type: '',
          group: '',
          unit: '',
          status: 'Active',
        });
      } catch (error) {
        logger.error('Failed to save product', error as Error);
      }
    },
    [editingProduct, createLubricantProduct, updateLubricantProduct, reset],
  );

  // Modal close handler
  const handleCloseModal = useCallback(() => {
    setShowAddProductModal(false);
    setEditingProduct(null);
    reset({
      productName: '',
      maker: '',
      category: '',
      type: '',
      group: '',
      unit: '',
      status: 'Active',
    });
  }, [reset]);

  return (
    <Stack gap={1} sx={{ height: '100%', minHeight: 0 }}>
      <PageHeader
        actions={actionBtns}
        options={breadcrumbOptions}
        isFilterActive={isFilterActive}
        resetFilters={() => setResetFiltersSignal(true)}
      />
      <TableCardContent>
        <ClientSideAgGrid
          rowData={lubricantProductsList}
          columnDefs={tableColumns as any}
          loading={isLoading}
          className="lubricant-products-grid"
          onFiltersActiveChange={(active: boolean) => {
            setIsFilterActive(active);
            setResetFiltersSignal(false);
          }}
          resetFiltersSignal={resetFiltersSignal}
        />
      </TableCardContent>

      {showAddProductModal && (
        <EditLubricantProductModal
          onClose={handleCloseModal}
          onSubmit={handleSubmit(onSubmit)}
          control={control}
          errors={errors}
          isEditMode={!!editingProduct}
          makersOptions={makersOptions}
          categoriesOptions={categoriesOptions}
          typesOptions={typesOptions}
          groupsOptions={groupsOptions}
          unitsOptions={unitsOptions}
          isSubmitting={isCreating || isUpdating}
        />
      )}

      {showDeleteConfirm && (
        <DeleteConfirmModal
          open={showDeleteConfirm}
          title={t('lubricants.deleteProduct')}
          description={t('common.deleteConfirmation')}
          isSubmitting={isDeleting}
          onClose={onCloseDeleteConfirm}
          onConfirm={handleConfirmDelete}
          confirmLabel={isDeleting ? t('common.deleting') : t('buttons.yes')}
        />
      )}
    </Stack>
  );
};

export default LubricantProductsContainer;
